/**
 * Schlüssel, die ein Konto mit dem Besuch davor verbinden.
 *
 * Der eigene Tracker kennt den Browser nur über die GA4-Client-ID (Cookie `_ga`),
 * Customer.io über das Cookie `_cioanonid`. Beides ist auf webmetic.de lesbar und
 * wird beim Signup mitgeschickt, damit Anzeigenklicks später einem Konto
 * zugeordnet werden können. Die Klick-IDs kommen mit Datum aus der
 * Touchpoint-Historie, weil Google und Microsoft sie nur ~90 Tage auflösen.
 */

export const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "msclkid", "li_fat_id", "fbclid", "dclid"] as const;

/** Touchpoint, wie ihn attribution.ts in localStorage ablegt (nur die Felder, die hier zählen). */
export interface TouchpointLike {
  timestamp?: string | null;
  landing_page?: string | null;
  utm_params?: Record<string, unknown> | null;
}

export interface ClickId {
  type: (typeof CLICK_ID_PARAMS)[number];
  value: string;
  timestamp: string | null;
  landing_page: string | null;
}

function readCookie(name: string, cookieString: string): string | null {
  for (const part of cookieString.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return null;
}

/** `_ga` = "GA1.1.313831857.1781117383" → "313831857.1781117383" */
export function gaClientId(cookieString: string): string | null {
  const raw = readCookie("_ga", cookieString);
  if (!raw) return null;
  const parts = raw.split(".");
  return parts.length >= 4 ? parts.slice(-2).join(".") : raw;
}

export function cioAnonymousId(cookieString: string): string | null {
  return readCookie("_cioanonid", cookieString);
}

/** Alle Klick-IDs aus den Touchpoints, älteste zuerst, ohne Duplikate. */
export function clickIdsFromTouchpoints(touchpoints: TouchpointLike[]): ClickId[] {
  const seen = new Set<string>();
  const out: ClickId[] = [];
  for (const tp of [...touchpoints].reverse()) {
    const params = tp?.utm_params;
    if (!params) continue;
    for (const type of CLICK_ID_PARAMS) {
      const value = params[type];
      if (typeof value !== "string" || !value || value === "true") continue;
      const key = `${type}:${value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const fallback = typeof params.timestamp === "string" ? params.timestamp : null;
      out.push({ type, value, timestamp: tp.timestamp ?? fallback, landing_page: tp.landing_page ?? null });
    }
  }
  return out;
}

export function attributionKeys(touchpoints: TouchpointLike[], cookieString = typeof document === "undefined" ? "" : document.cookie) {
  return {
    gaClientId: gaClientId(cookieString),
    cioAnonymousId: cioAnonymousId(cookieString),
    clickIds: clickIdsFromTouchpoints(touchpoints),
  };
}
